import React, { useRef, useEffect, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import axios from "axios";
import "../App.css";
import { FaTrashAlt } from "react-icons/fa";
import { IoArrowBackSharp } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { Button } from "react-bootstrap";
import { useTheme } from "./ThemeContext";

function SignaturePad() {
  const sigCanvas = useRef();
  const [id, setID] = useState("");
  const [signatures, setSignatures] = useState([]);
  const navigate = useNavigate();
  const { isDark } = useTheme();

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user?.id) {
      setID(user.id);
    }
  }, []);

  useEffect(() => {
    if (!id) return;

    const fetchSignatures = async () => {
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("No auth token found in localStorage");
      }

      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API}/getSignature`,
          {
            params: { user_id: id },
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (res.data.success) {
          setSignatures(res.data.data);
        }
      } catch (err) {
        console.error("Fetch error:", err);
      }
    };

    fetchSignatures();
  }, [id]);

  const handleSave = async () => {
    if (!sigCanvas.current || sigCanvas.current.isEmpty()) {
      alert("Please sign before submitting.");
      return;
    }

    const token = localStorage.getItem("authToken");
    if (!token) {
      throw new Error("No auth token found in localStorage");
    }

    const signatureData = sigCanvas.current.toDataURL("image/png");

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API}/saveSignature`,
        {
          id: id,
          signature: signatureData,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        alert("Signature saved!");
        sigCanvas.current.clear();
        window.location.reload();
      }
    } catch (error) {
      console.error("API error:", error);
      alert("Server error while saving signature.");
    }
  };

  const handleDelete = async (signatureId) => {
    if (window.confirm("Are you sure you want to delete this signature?")) {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) {
          throw new Error("No auth token found in localStorage");
        }

        const res = await axios.post(
          `${process.env.REACT_APP_API}/deleteSignature`,
          {
            id: signatureId,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (res.data.success) {
          // Refresh the list without reloading the whole page
          setSignatures((prev) =>
            prev.filter((s) => s.signature_id !== signatureId)
          );
        } else {
          alert("Failed to delete signature.");
        }
      } catch (err) {
        console.error("Delete error:", err);
        alert("Server error while deleting.");
      }
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="signature-container text-center">
      <div
        className="d-flex align-items-center justify-content-between mb-3"
        style={{ width: "100%" }}
      >
        <Button
          variant={isDark ? "outline-dark" : "outline-light"}
          size="sm"
          onClick={handleBack}
          style={{
            fontSize: "0.9rem",
            color: "#B31E8D",
            border: isDark ? "1px solid #202020" : "1px solid #EAECF0",
          }}
        >
          <IoArrowBackSharp
            style={{
              marginRight: "5px",
              color: isDark ? "#fff" : "#B31E8D",
            }}
            size={20}
          />
        </Button>

        <div
          style={{
            flex: 1,
            textAlign: "center",
            marginRight: "40px",
            color: isDark ? "#fff" : "#000",
            padding: "10px",
            borderRadius: "8px",
          }}
        >
          <h3 className="m-0">Signature Pad</h3>
        </div>
      </div>

      <SignatureCanvas
        ref={sigCanvas}
        penColor={isDark ? "white" : "black"}
        canvasProps={{
          width: 500,
          height: 200,
          className: "signature-canvas",
        }}
      />

      <div className="mt-3">
        <button
          onClick={() => sigCanvas.current.clear()}
          className="btn btn-secondary me-2"
          style={{ width: "100px" }}
        >
          Clear
        </button>
        <button
          onClick={handleSave}
          className="btn"
          style={{ background: "#B31E8D", color: "white", width: "100px" }}
        >
          Save
        </button>
      </div>

      <div className="mt-4 d-flex flex-column align-items-center">
        {signatures.map((item, index) => (
          <div
            key={index}
            className={`d-flex justify-content-between align-items-center p-3 m-2 shadow-sm rounded ${
              isDark ? "bg-dark text-white" : "bg-white text-dark"
            }`}
            style={{ width: "450px" }}
          >
            {/* Left: Image + Info */}
            <div className="d-flex align-items-center">
              <img
                className="signature-preview"
                src={item.signature}
                alt="signature"
                style={{
                  width: "200px",
                  height: "100px",
                  objectFit: "contain",
                  marginRight: "10px",
                }}
              />
              <div className="text-start">
                <strong style={{ color: isDark ? "#fff" : "#000" }}>
                  {item.display_name}
                </strong>
                <div
                  className="mt-3"
                  style={{ color: isDark ? "#aaa" : "#6c757d" }} // matches Bootstrap `text-muted`
                >
                  {item.date}
                </div>
              </div>
            </div>

            <FaTrashAlt
              size={20}
              className="text-danger"
              style={{
                cursor: "pointer",
                color: isDark ? "#ff6b6b" : undefined,
              }}
              onClick={() => handleDelete(item.signature_id)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export default SignaturePad;
